'use client'

import type { GroupProps, SlotRecipeProps } from '@chakra-ui/react'
import { Avatar as ChakraAvatar, Group } from '@chakra-ui/react'
import * as React from 'react'

import { assertNever } from '@/utils/assertNever'

type ImageProps = React.ImgHTMLAttributes<HTMLImageElement>

export interface AvatarProps extends ChakraAvatar.RootProps {
  name?: string
  src?: string
  srcSet?: string
  loading?: ImageProps['loading']
  icon?: React.ReactElement
  fallback?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  function Avatar(props, ref) {
    const {
      name,
      src,
      srcSet,
      loading,
      icon,
      fallback,
      children,
      size: sizeProps = 'md',
      ...rest
    } = props
    const size = (() => {
      switch (sizeProps) {
        case 'sm':
          return '24px'
        case 'md':
          return '32px'
        case 'lg':
          return '40px'
        default:
          return assertNever(sizeProps)
      }
    })()
    const fontSize = (() => {
      switch (sizeProps) {
        case 'sm':
          return 'xs'
        case 'md':
          return 'sm'
        case 'lg':
          return 'md'
        default:
          return assertNever(sizeProps)
      }
    })()
    return (
      <ChakraAvatar.Root
        ref={ref}
        {...rest}
        w={size}
        h={size}
        fontSize={fontSize}
      >
        <AvatarFallback name={name} icon={icon} fontSize={fontSize}>
          {fallback}
        </AvatarFallback>
        <ChakraAvatar.Image src={src} srcSet={srcSet} loading={loading} />
        {children}
      </ChakraAvatar.Root>
    )
  },
)

interface AvatarFallbackProps extends ChakraAvatar.FallbackProps {
  name?: string
  icon?: React.ReactElement
}

const AvatarFallback = React.forwardRef<HTMLDivElement, AvatarFallbackProps>(
  function AvatarFallback(props, ref) {
    const { name, icon, children, ...rest } = props
    return (
      <ChakraAvatar.Fallback color="gray.700" ref={ref} {...rest}>
        {children}
        {name != null && children == null && <>{getInitials(name)}</>}
        {name == null && children == null && (
          <ChakraAvatar.Icon asChild={!!icon}>{icon}</ChakraAvatar.Icon>
        )}
      </ChakraAvatar.Fallback>
    )
  },
)

function getInitials(name: string) {
  // サロゲートペアを考慮して、Array.from()を使用して文字列を分割（絵文字などを考慮）
  return name ? Array.from(name)[0] : ''
}

interface AvatarGroupProps extends GroupProps, SlotRecipeProps<'avatar'> {}

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  function AvatarGroup(props, ref) {
    const { size, variant, borderless, ...rest } = props
    return (
      <ChakraAvatar.PropsProvider value={{ size, variant, borderless }}>
        <Group gap="0" spaceX="-3" ref={ref} {...rest} />
      </ChakraAvatar.PropsProvider>
    )
  },
)
